import { FoodsIcon, SecondaryTab, SecondaryTabs } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";

type MandatoryTabKey = "distribution";
type OptionalTabKeys = "comparison" | "evolution";
export type TabKeys = OptionalTabKeys | MandatoryTabKey;
export type Tabs = TabKeys | null;

export type TabConfig = Partial<Record<OptionalTabKeys, TabButton>> & Record<MandatoryTabKey, TabButton>;

type TabButton = {
    name?: string;
    icon?: JSX.Element;
    link: (scoreId: string | number) => string;
};

type ScoreDetailTabsProps = {
    selectedTab: Tabs;
};
export const ScoreDetailTabs = (props: ScoreDetailTabsProps) => {
    const { selectedTab } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureActivated } = useApplicationParameters();
    const { drilldownState, scoreDetail, tabConfig } = useScoreDetailContext();

    // Done waiting for SecondaryTabs upgrade.
    // Because I want to match the URL with the tab, BUT need the display name
    const tabMapping: Record<TabKeys, number> = {
        distribution: 0,
        comparison: 1,
        evolution: 2,
    };

    const tabs: SecondaryTab[] = [];
    const distributionItem = tabConfig["distribution"];
    if (distributionItem && isFeatureActivated("FS-score-details-distribution")) {
        tabs.push({
            id: tabMapping["distribution"],
            name: distributionItem.name ?? t("distribution_variant"),
            onClick: () => {
                drilldownState.reset();
                const url = distributionItem.link(scoreDetail.id);
                navigate(url);
            },
            component: <Outlet />,
            icon: distributionItem.icon ?? <FoodsIcon size={2} icon="repartition" />,
        });
    }

    const comparisonItem = tabConfig["comparison"];
    if (comparisonItem && isFeatureActivated("FS-score-details-comparison")) {
        tabs.push({
            id: tabMapping["comparison"],
            name: comparisonItem.name ?? t("distribution_comparison"),
            component: <Outlet />,
            onClick: () => {
                drilldownState.reset();
                const url = comparisonItem.link(scoreDetail.id);
                navigate(url);
            },
            icon: comparisonItem.icon ?? <FoodsIcon size={2} icon="comparison" />,
        });
    }

    const evolutionItem = tabConfig["evolution"];
    if (evolutionItem && isFeatureActivated("FS-score-details-evolution")) {
        tabs.push({
            id: tabMapping["evolution"],
            name: evolutionItem.name ?? "evolution",
            component: <Outlet />,
            onClick: () => {
                drilldownState.reset();
                const url = evolutionItem.link(scoreDetail.id);
                navigate(url);
            },
            icon: evolutionItem.icon ?? <FoodsIcon size={2} icon="evolution" />,
        });
    }

    const selectedTabId = selectedTab === null ? 0 : tabMapping[selectedTab];
    // const _selectedTab = tabs.find((tab) => tab.name === selectedTab);
    // const selectedTabId = _selectedTab === undefined ? 0 : _selectedTab.id;
    return (
        <SecondaryTabs
            tabs={tabs}
            // This logic would need to be refined, but works today
            selectedTab={(tab) => tab.id === selectedTabId}
        />
    );
};
